import { useEffect, useRef, useState } from 'react'
import { User } from '../../../data/models/user';
import { Billing } from '../../../data/models/billing';
import UserSelect from '../../components/menu/Form/select/UserSelect';
import { App, Button, Checkbox, Col, Divider, Empty, Input, Modal, notification, Progress, Row, Select, Typography } from 'antd';
import { PlusOutlined, SaveOutlined } from '@ant-design/icons';
import { makeRequest } from '../../helpers/axios-manipulator';
import { getCurrentUtcDate } from '../../helpers/current-utc-date-generator'
import React from 'react';
import Title from 'antd/es/typography/Title';
import { USER_TYPES } from '../../../constants';
import RepresentationContract from '../../components/Billing/RepresentationContract';
import ArtistRepresentationContract from '../../components/Billing/ArtistRepresentationContract';
import { ArtistRep } from '../../../data/models/artist-rep';
import { green, red, orange } from '@ant-design/colors';
import NewUserModal from '../../components/NewUserModal';
import SuccessIcon from '../../assets/icons/success-icon.svg';
import CopyIcon from '../../assets/icons/copy-icon.svg';

const { Text } = Typography;

const Users = () => {
  const [data, setData] = useState<User>({} as User);
  const [newSelectedUser, setNewSelectedUser] = useState<string>()
  const [contracts, setContracts] = useState<ArtistRep[]>([]);
  const [billing, setBilling] = useState<Billing>({} as Billing);
  const [contractsKey, setContractsKey] = useState<number>(1)
  const [openNewUserModal, setOpenNewUserModal] = useState(false);
  const [enableRecoveryModal, setEnableRecoveryModal] = useState(false);
  const [editedUserUsername, setEditedUserUsername] = useState()
  const [clickLink, setClickLink] = useState(false);
  const [recoveryLink, setRecoveryLink] = useState("")

  const inputRef = useRef<HTMLInputElement | null>(null);

  const contractsPercent = contracts.reduce((sum, contract) => sum + contract.fee, 0);

  const [hasNewContract, setHasNewContract] = useState(false)
  const isAnyEditing = contracts.some(contract => contract.editing === true);
  const [loading, setLoading] = useState(false);

  const { message } = App.useApp();
  const [api, contextHolder] = notification.useNotification();



  const save = async () => {
    const hasDuplicateDealer = contracts.some((contract, index, array) => {
      return array.findIndex(c => c.rep_id === contract.rep_id) !== index;
    });
    const hasDuplicateArtist = contracts.some((contract, index, array) => {
      return array.findIndex(c => c.artist_id === contract.artist_id) !== index;
    });
    if (data?.username?.length && data?.username?.length > 20)
      return message.error('username must have a maximum of 20 characters')
    if (hasDuplicateDealer && data.type === 1)
      return api['error']({
        duration: 5,
        message: 'Error creating contract',
        description:
          'It is not possible to add the same dealer more than once',
      });
    if (hasDuplicateArtist && (data.type === 3 || data.type === 4))
      return api['error']({
        duration: 5,
        message: 'Error creating contract',
        description:
          'It is not possible to add the same artist more than once',
      });
    setLoading(true)

    try {
      const response = await makeRequest({
        origin: window.location.origin,
        method: 'put',
        headers: {
          'Content-Type': 'application/json',
        },
        url: `/api-user/${data?.id}`,
        data: { user: data },
      });
      setData(response);
      setNewSelectedUser(response.username)
      if (data.username !== editedUserUsername) {
        const resp = await makeRequest({
          origin: window.location.origin,
          method: 'post',
          headers: {
            'Content-Type': 'application/json',
          },
          url: '/api-onboarding-username',
          data: { old_username: editedUserUsername, new_username: data.username },
        });
      }
      message.success('User updated with success!');
      if (data.type !== 2 && contracts) {
        const contractsResponse = await makeRequest({
          origin: window.location.origin,
          method: 'post',
          headers: {
            'Content-Type': 'application/json',
          },
          url: '/api-manipulate-contracts',
          data: { contracts },
        });
        setContracts(contractsResponse)
        message.success('Contracts created with success!');
      }
      if (billing) {
        const response = await makeRequest({
          origin: window.location.origin,
          method: 'put',
          headers: {
            'Content-Type': 'application/json',
          },
          url: `/api-update-billing`,
          data: { billing },
        });
        setBilling(response)
      }
      setLoading(false)
    } catch (error) {
      message.error('something went wrong during the update')
      setLoading(false)
    }
  }

  const addEmptyContract = () => {
    const newContract = {
      id: "",
      artist_id: data.type === 1 ? data?.id : "",
      dealer_profile: "",
      dealer_username: "",
      rep_id: (data.type === 3 || data.type === 4) ? data?.id : "",
      rep_type: 4,
      status: "active",
      fee: 0,
      creation_date: getCurrentUtcDate(),
      last_updated_Date: getCurrentUtcDate(),
      last_updated_by: data?.id,
      editing: true
    };
    setContracts(prevContracts => [...prevContracts, newContract]);
    setHasNewContract(true)
  };

  const getRelatedContracts = async () => {
    try {
      const response = await makeRequest({
        origin: window.location.origin,
        method: 'get',
        url: `/api-available-contracts/${data?.id}/${data?.type}`,
        headers: {},
      });
      setContracts(response)
    } catch (error) {

    }
  }

  const getBilling = async () => {
    try {
      const response = await makeRequest({
        origin: window.location.origin,
        method: 'get',
        url: `/api-billing/${data?.id}`,
        headers: {},
      });
      setBilling(response)
    } catch (error) {

    }
  }

  const recoveryAccount = async () => {
    if (!data.email || data.email === "")
      return api.warning({
        message: `Email Required`,
        description: `The user profile must include an email address to enable account recovery.`,
        placement: "bottomRight",
      });
    try {
      const { user } = await makeRequest({
        origin: window.location.origin,
        method: 'post',
        headers: {
          'Content-Type': 'application/json',
        },
        url: '/api-onboarding/recovery',
        data: {
          userId: data.id,
          username: data.profile_name,
          account_type: data.type,
          email: data.email,
        }
      });
      message.success('Recovery created with success!');
      setRecoveryLink(`${(process.env.REACT_APP_ENV === 'development' || process.env.REACT_APP_ENV === 'test') ? 'https://dev.ujamaa.digital' : process.env.REACT_APP_ENV === 'production' ? 'https://app.ujamaa.digital' : 'https://qa.ujamaa.digital'}/${user.username}?onboardingId=${user.id}&notificationEmail=${user.notification_email}`)
      setEnableRecoveryModal(true)

    } catch (error) {
      message.error('Something got wrong during the recovery creation!');
    }
  }

  const copyInputContent = () => {
    if (!inputRef.current) return;
    inputRef.current.select();

    try {
      document.execCommand('copy');
      setClickLink(true);
    } catch (err) {
      console.error('Error copying: ', err);
    }
  };

  useEffect(() => {
    if (data && Object.keys(data)?.length > 0) {
      getRelatedContracts();
      if (data?.type === 4) getBilling();
      setHasNewContract(false);
    }
  }, [data])

  useEffect(() => { setContractsKey(contractsKey + 1) }, [contracts])


  return (
    <>
      {contextHolder}
      <Button type="primary" className='mb-6' style={{ float: "right" }} onClick={() => setOpenNewUserModal(true)}>New user</Button>
      {data && Object.keys(data)?.length !== 0 &&
        <Button type="primary" className='mb-6' style={{ float: "right", marginRight: 10 }} onClick={recoveryAccount}>Recover account</Button>
      }
      <UserSelect update={loading} className='mb-5' setData={setData} data={data} selectedUser={newSelectedUser} saveUsername={setEditedUserUsername} />

      <Text>User Type</Text>
      <Input disabled className='mb-5' value={USER_TYPES[data?.type]}
        placeholder="User type" />

      <Text>Username</Text>
      <Input disabled={data && Object.keys(data)?.length === 0} className='mb-5' value={data?.username || ''}
        count={{
          show: true,
          max: 20,
        }}
        onChange={(e) => setData({ ...data, username: e.target.value })}
        placeholder="Username" />

      <Text>Display name</Text>
      <Input disabled={data && Object.keys(data)?.length === 0} className='mb-5' value={data?.display_name || ''}
        onChange={(e) => setData({ ...data, display_name: e.target.value })}
        placeholder="Display name" />

      <Text>wallet address</Text>
      <Input disabled={data && Object.keys(data)?.length === 0} className='mb-5' value={data?.wallet_address || ''}
        onChange={(e) => setData({ ...data, wallet_address: e.target.value })}
        placeholder="wallet address" />

      <Text>Google id</Text>
      <Input disabled={data && Object.keys(data)?.length === 0} className='mb-5' value={data?.google_id || ''}
        onChange={(e) => setData({ ...data, google_id: e.target.value })}
        placeholder="google id" />

      <Text>Email</Text>
      <Input disabled={data && Object.keys(data)?.length === 0} className='mb-5' value={data?.email || ''}
        onChange={(e) => setData({ ...data, email: e.target.value })}
        placeholder="Email" />

      <Text>profile name</Text>
      <Input disabled={data && Object.keys(data)?.length === 0} className='mb-5' value={data?.profile_name || ''}
        onChange={(e) => setData({ ...data, profile_name: e.target.value })}
        placeholder="profile name" />

      <Text>stripe id</Text>
      <Input disabled={data && Object.keys(data)?.length === 0} className='mb-5' value={data?.stripe_id_acct || ''}
        onChange={(e) => setData({ ...data, stripe_id_acct: e.target.value })}
        placeholder="stripe id" />

      <Text>Account id</Text>
      <Input disabled={data && Object.keys(data)?.length === 0} className='mb-5' value={data?.id || ''}
        onChange={(e) => setData({ ...data, id: e.target.value })}
        placeholder="Account id" />

      {data && data?.type === 4 &&
        <>
          <Text>business premium</Text>
          <Input
            disabled={billing?.premium === undefined || billing?.premium === null}
            className="mb-5"
            value={billing?.premium ?? ''}
            onChange={(e) => setBilling({ ...billing, premium: e.target.value === '' ? undefined : parseInt(e.target.value) || 0 })}
          />

          <Divider className="my-0" orientation="left" orientationMargin="0">
            <Text strong>escrow account</Text> - <Text type={billing?.is_escrow_enabled ? "success" : "danger"}>{billing?.is_escrow_enabled ? "Active" : "Inactive"}</Text>
          </Divider>
          <Row gutter={8}>
            <Col span={24} md={12}>
              <Text>escrow id</Text>
              <Input disabled className='mb-5' value={billing?.escrow_id || ''} />
            </Col>
            <Col span={24} md={12}>
              <Text>escrow premium</Text>
              <Input disabled className='mb-5' value={billing?.escrow_premium || ''} />
            </Col>
          </Row>
        </>
      }

      {data && data?.type === 1 &&
        <>
          <Row>
            <Col span={12}>
              <Title className='mt-0' level={4}>Contracts</Title>
              <Progress percent={contractsPercent} steps={10}
                status="active"
                strokeColor={[green[2], green[3], green[4], orange[3], orange[4], orange[5], red[4], red[5], red[6], red[7]]}
              />
              <p className='mt-0'>
                <Text className='mt-0' type="secondary">artist's committed percentage</Text>
              </p>
            </Col>
            <Col span={12}>
            </Col>
          </Row >
          {
            Object.keys(contracts).length === 0 ? <Empty
              imageStyle={{ height: 60 }}
              description={
                <Typography.Text>
                  no contract found
                </Typography.Text>
              }
            >
              <Button onClick={addEmptyContract}>Create Now</Button>
            </Empty> :
              <div key={contractsKey}>
                {!hasNewContract && contracts.length <= 4 &&
                  <Button
                    className='mt-4'
                    type="primary" onClick={addEmptyContract}>Create contract</Button>
                }
                {contracts?.map((contract, i) => (
                  <RepresentationContract
                    contract={contract}
                    contracts={contracts}
                    setContracts={setContracts}
                    index={i}
                    hasNewContract={hasNewContract}
                    setHasNewContract={setHasNewContract}
                    loading={false}
                    totalUsedFee={contractsPercent} />
                ))}
              </div>
          }
        </>
      }

      {data && (data?.type === 3 || data?.type === 4) &&
        <>
          <Row>
            <Col span={12}>
              <Title className='mt-0' level={4}>Contracts</Title>
            </Col>
            <Col span={12}>
            </Col>
          </Row >
          {
            Object.keys(contracts).length === 0 ? <Empty
              imageStyle={{ height: 60 }}
              description={
                <Typography.Text>
                  no contract found
                </Typography.Text>
              }
            >
              <Button onClick={addEmptyContract}>Create Now</Button>
            </Empty> :
              <div key={contractsKey}>
                {!hasNewContract &&
                  <Button
                    className='mt-4'
                    type="primary" onClick={addEmptyContract}>Create contract</Button>
                }
                {contracts?.map((contract, i) => (
                  <ArtistRepresentationContract
                    contract={contract}
                    contracts={contracts}
                    setContracts={setContracts}
                    index={i}
                    hasNewContract={hasNewContract}
                    setHasNewContract={setHasNewContract}
                    loading={false} />
                ))}
              </div>
          }
        </>
      }

      <Button
        className='mt-4'
        disabled={(data && Object.keys(data)?.length === 0) || hasNewContract || isAnyEditing}
        type="primary"
        icon={<SaveOutlined />}
        loading={loading}
        onClick={() => save()}
      >
        Save
      </Button>

      {/* New user modal */}
      <NewUserModal setUserSelected={setNewSelectedUser} open={openNewUserModal} setOpen={setOpenNewUserModal} setParentLoading={setLoading} />
      <Modal title="Account Recovery" open={enableRecoveryModal} onCancel={() => setEnableRecoveryModal(false)}
        footer={
          <Button type="primary" onClick={() => setEnableRecoveryModal(false)}>
            Ok
          </Button>
        }>
        <p>You have generated an account recovery link. If the user does not have one yet, copy the link below and send it.</p>
        <p>Or you can clear the user's wallet_address and google_id fields to use the "Re-invite" functionality from the onboarding menu to send an account claim email.</p>
        <div className="relative flex w-full mb-4" style={{ background: '#CCCCCC' }}>
          <input
            ref={inputRef}
            className="flex items-center w-full h-8 border py-2 px-1 border-solid border-gray-border text-gray-text text-xs p-2"
            defaultValue={recoveryLink}
            readOnly
          />
          <img onClick={copyInputContent} style={{ padding: '.5rem' }} width={15} className="object-contain mx-1 my-auto cursor-pointer"
            src={clickLink ? SuccessIcon : CopyIcon} alt="status icon" />
        </div>
      </Modal>
    </>
  )
}

export default Users